/**
 * Warm introductions nobody has used.
 *
 * Twenty holds 85 contacts at Saudi Tourism Authority and 26 at Boehringer, most arrived by
 * calendar sync and most never written to by anyone. Each account's relationship currently runs
 * through one or two threads. This is the only panel on the deck that is about OPPORTUNITY rather
 * than obligation, which is why it names people rather than counting them: "23 untouched at STA"
 * is a statistic, and "Fahad Al-Otaibi, Layla Nasser" is something he can act on this morning.
 *
 * IT RANKS NOBODY AND SUGGESTS NOBODY. Any ordering by seniority or fit would be invented from a
 * job title, and an invented recommendation on a panel of otherwise-counted facts is how the whole
 * board loses its credibility. Names are alphabetical.
 *
 * The count beside each account is a FRACTION of what the CRM holds, so a row reads as "9 of 85
 * never written to" rather than as a bare number that could mean anything.
 */

import { useCallback, useEffect, useState, type JSX } from 'react';
import { Icon } from './Icon.js';
import { fetchUntouched, type UntouchedResult } from './api.js';

export function UntouchedPanel(): JSX.Element | null {
  const [result, setResult] = useState<UntouchedResult | null>(null);

  const load = useCallback(async () => {
    setResult(await fetchUntouched());
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Still fetching. No panel until it answers, so an empty box never reads as an empty answer.
  if (result === null) return null;

  if (result.state === 'unavailable') {
    return (
      <section className="deck-panel" data-hue="waiting">
        <header className="deck-panel-head">
          <Icon name="waiting" size={16} className="deck-panel-icon" />
          <h2 className="deck-panel-title">Never written to</h2>
        </header>
        <div className="deck-panel-body">
          <p className="deck-panel-note">Not known: {result.message}</p>
        </div>
      </section>
    );
  }

  /*
   * Genuinely nobody untouched is a real answer and a surprising one on this CRM, so it is stated
   * rather than rendered as an absent panel.
   */
  if (result.accounts.length === 0) {
    return (
      <section className="deck-panel" data-hue="waiting">
        <header className="deck-panel-head">
          <Icon name="waiting" size={16} className="deck-panel-icon" />
          <h2 className="deck-panel-title">Never written to</h2>
        </header>
        <div className="deck-panel-body">
          <p className="deck-panel-note">
            Everyone the CRM holds at your live accounts has been in correspondence at least once.
          </p>
        </div>
      </section>
    );
  }

  const total = result.accounts.reduce((n, a) => n + a.untouched.length, 0);

  return (
    <section className="deck-panel" data-hue="waiting">
      <header className="deck-panel-head">
        <Icon name="waiting" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">Never written to</h2>
        <span className="deck-count">{total}</span>
      </header>
      <div className="deck-panel-body">
        <p className="deck-panel-note">
          People your CRM holds at live accounts who have never been in correspondence. Ordered by
          how many each account has, not by any judgement about who is worth contacting.
        </p>
        <ul className="mo-untouched">
          {result.accounts.map((a) => (
            <li key={a.account} className="mo-untouched-row">
              <div className="mo-untouched-head">
                <a href={a.twentyUrl} target="_blank" rel="noreferrer" className="mo-untouched-account">
                  {a.account}
                </a>
                {/* A fraction, so the number means something on its own. */}
                <span className="mo-untouched-count">
                  {a.untouched.length} of {a.totalInCrm} never written to
                </span>
              </div>
              <p className="mo-untouched-names">{a.untouched.map((p) => p.name).join(', ')}</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
